import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';

import { authenticateUser } from 'src/auth';
import FlashesRenderer from 'src/components/Flashes/FlashesRenderer';
import HTTP404 from 'src/components/HTTP404';
import LayoutErrorBoundary from 'src/components/Layout/LayoutErrorBoundary';
import Loading from 'src/components/Loading';
import CustomerPortfolioNavigation from 'src/components/Navigation/CustomerPortfolioNavigation';
import Footer from 'src/enosikit/components/Footer';
import { withTracingHOC } from 'src/tracing';

/**
 * Renders the authenticated page shell for a customer portfolio view, including the
 * navigation bar, flash messages, page content, and footer. Returns an HTTP 404
 * page when the portfolio is absent, or a loading spinner while authentication is
 * being resolved.
 * @param {object} props
 * @param {React.ReactNode|React.ReactNode[]} props.children - Page content to render
 * inside the layout.
 * @param {object|null} props.portfolio - Relay fragment data for the active portfolio.
 * @param {object} props.router - React Router object used by authenticateUser for redirects.
 * @param {object|null} props.viewer - Relay fragment data for the authenticated viewer.
 * @returns {React.ReactElement} The composed portfolio layout, a loading spinner, or
 * an HTTP 404 page.
 */
function CustomerPortfolioLayoutContent(props) {
  const {
    children, portfolio, router, viewer,
  } = props;

  const intl = useIntl();

  if (!portfolio) {
    return <HTTP404 />;
  }

  if (!authenticateUser(intl, viewer, null, router)) {
    return <Loading />;
  }

  const portfolioTitle = portfolio.title || intl.formatMessage({ id: 'layout.customer_layout.customer_portfolio_layout.page.fallback_portfolio.title', defaultMessage: 'Portfolio' });
  const { portfolios } = viewer;
  const pageTitle = intl.formatMessage({ id: 'layout.customer_layout.customer_portfolio_layout.page.title', defaultMessage: 'Enosi - Portfolio: {portfolioTitle}' }, { portfolioTitle });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <div className="flex flex-column" style={{ minHeight: '80vh' }}>
        <CustomerPortfolioNavigation
          viewer={viewer}
          portfolios={portfolios}
          portfolio={portfolio}
        />
        <section id="main">
          <FlashesRenderer />
          <div className="container">
            <div className="flex-auto">{children}</div>
          </div>
        </section>
      </div>

      <Footer versionUI={VERSION} />
    </>
  );
}

const customerPortfolioLayoutPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  portfolio: PropTypes.shape({
    uuid: PropTypes.string,
    title: PropTypes.string,
  }),
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.shape({
    id: PropTypes.string,
    portfolios: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }),
};

const customerPortfolioLayoutDefaultProps = {
  portfolio: null,
  viewer: null,
};

CustomerPortfolioLayoutContent.propTypes = customerPortfolioLayoutPropTypes;
CustomerPortfolioLayoutContent.defaultProps = customerPortfolioLayoutDefaultProps;

// Intentionally omit @throwOnFieldError here: this layout fragment owns broad page-shell
// concerns such as the <title> tag and auth shell inputs like portfolio.title and
// viewer.viewerUser, so it stays defensive by design with guards and localized fallback
// values when data is absent or degraded. By contrast, the strict
// CustomerPortfolioNavigation fragments are a separate failure domain where corrupted data is
// fatal and should throw. Keeping the layout fragment non-throwing prevents unnecessary
// top-level crashes for fields that can be handled gracefully, while LayoutErrorBoundary
// remains positioned to catch render-phase exceptions from descendant fragments that do opt
// into @throwOnFieldError and to preserve the layout as a stable host for fallback UI.
const CustomerPortfolioLayoutFragmentContainer = createFragmentContainer(
  CustomerPortfolioLayoutContent,
  {
    viewer: graphql`
      fragment CustomerPortfolioLayout_viewer on Viewer {
        id
        viewerUser {
          uuid
          type
        }
        ...CustomerPortfolioNavigation_viewer
        portfolios {
          ...CustomerPortfolioNavigation_portfolios
        }
      }
    `,
    portfolio: graphql`
      fragment CustomerPortfolioLayout_portfolio on Portfolio {
        title
        ...CustomerPortfolioNavigation_portfolio
      }
    `,
  },
);

const TracedCustomerPortfolioLayout = withTracingHOC(
  'CustomerPortfolioLayout',
  CustomerPortfolioLayoutFragmentContainer,
);

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - CustomerPortfolioLayout component
 */
function CustomerPortfolioLayout(props) {
  const {
    children, portfolio, router, viewer,
  } = props;

  return (
    <LayoutErrorBoundary>
      <TracedCustomerPortfolioLayout
        portfolio={portfolio}
        router={router}
        viewer={viewer}
      >
        {children}
      </TracedCustomerPortfolioLayout>
    </LayoutErrorBoundary>
  );
}

CustomerPortfolioLayout.propTypes = customerPortfolioLayoutPropTypes;
CustomerPortfolioLayout.defaultProps = customerPortfolioLayoutDefaultProps;

export default CustomerPortfolioLayout;
